import { useState, useEffect } from 'react'
import { CreateGearNeedRequest, GearNeed } from '@/shared/api/gearNeeds'
import { Button } from '@/ui/design-system'
import { Label, Input, Textarea } from '@/ui/components'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/components'
import { Checkbox } from '@/ui/components'
import { cn } from '@/shared/utils'

export const GEAR_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'electronic', label: 'Electronic' },
  { value: 'cooking', label: 'Cooking' },
  { value: 'safety', label: 'Safety' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'communication', label: 'Communication' },
  { value: 'power', label: 'Power' },
  { value: 'storage', label: 'Storage' },
  { value: 'tools', label: 'Tools' },
  { value: 'other', label: 'Other' }
]

interface AddGearNeedFormProps {
  onSave: (data: CreateGearNeedRequest) => Promise<void>
  onCancel: () => void
  isLoading: boolean
  editingGearNeed?: GearNeed | null
  /** When true, show compact layout suitable for right panel */
  compact?: boolean
}

export function AddGearNeedForm({
  onSave,
  onCancel,
  isLoading,
  editingGearNeed,
  compact = false
}: AddGearNeedFormProps) {
  const [formData, setFormData] = useState<CreateGearNeedRequest>({
    name: '',
    description: '',
    category: 'general',
    quantity_needed: 1,
    is_essential: false,
    notes: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (editingGearNeed) {
      setFormData({
        name: editingGearNeed.name,
        description: editingGearNeed.description || '',
        category: editingGearNeed.category,
        quantity_needed: editingGearNeed.quantity_needed,
        is_essential: editingGearNeed.is_essential,
        notes: editingGearNeed.notes || ''
      })
    } else {
      setFormData({
        name: '',
        description: '',
        category: 'general',
        quantity_needed: 1,
        is_essential: false,
        notes: ''
      })
    }
    setErrors({})
  }, [editingGearNeed])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const newErrors: Record<string, string> = {}
    if (!formData.name?.trim()) {
      newErrors.name = 'Gear name is required'
    }
    if (!formData.quantity_needed || formData.quantity_needed < 1) {
      newErrors.quantity_needed = 'Quantity must be at least 1'
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      await onSave(formData)
      setErrors({})
    } catch (error) {
    }
  }

  const handleChange = (field: keyof CreateGearNeedRequest, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('space-y-4', compact && 'space-y-3')}
    >
      <div className="space-y-2">
        <Label htmlFor="gear-name" className="text-xs font-medium text-earth-500">
          Gear Name *
        </Label>
        <Input
          id="gear-name"
          value={formData.name ?? ''}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder="e.g., Starlink, Camping chairs, First aid kit"
          disabled={isLoading}
          className={cn(errors.name && 'border-destructive')}
        />
        {errors.name && (
          <p className="text-sm text-destructive">{errors.name}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="gear-description" className="text-xs font-medium text-earth-500">
          Description
        </Label>
        <Textarea
          id="gear-description"
          value={formData.description ?? ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="Optional description of the gear item"
          rows={compact ? 2 : 3}
          disabled={isLoading}
          className="resize-none"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="gear-category" className="text-xs font-medium text-earth-500">
          Category
        </Label>
        <Select
          value={formData.category ?? 'general'}
          onValueChange={(v) => handleChange('category', v)}
          disabled={isLoading}
        >
          <SelectTrigger id="gear-category" className={cn(errors.category && 'border-destructive')}>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {GEAR_CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="gear-quantity" className="text-xs font-medium text-earth-500">
            Quantity Needed *
          </Label>
          <Input
            id="gear-quantity"
            type="number"
            min={1}
            value={formData.quantity_needed ?? 1}
            onChange={(e) => handleChange('quantity_needed', parseInt(e.target.value, 10) || 1)}
            disabled={isLoading}
            className={cn(errors.quantity_needed && 'border-destructive')}
          />
          {errors.quantity_needed && (
            <p className="text-sm text-destructive">{errors.quantity_needed}</p>
          )}
        </div>

        <div className="flex items-end pb-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="gear-essential"
              checked={formData.is_essential ?? false}
              onChange={(e) => handleChange('is_essential', e.target.checked)}
              disabled={isLoading}
            />
            <Label htmlFor="gear-essential" className="text-sm font-medium text-earth-700 cursor-pointer">
              Essential gear
            </Label>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="gear-notes" className="text-xs font-medium text-earth-500">
          Notes
        </Label>
        <Textarea
          id="gear-notes"
          value={formData.notes ?? ''}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Any additional notes about this gear need"
          rows={compact ? 1 : 2}
          disabled={isLoading}
          className="resize-none"
        />
      </div>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading} className="gap-2">
          {isLoading ? (
            <>
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              {editingGearNeed ? 'Saving...' : 'Adding...'}
            </>
          ) : (
            editingGearNeed ? 'Save Changes' : 'Add Gear Need'
          )}
        </Button>
      </div>
    </form>
  )
}
